import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { createSafeCloseHandler } from '../../utils/modalUtils';
import { AddEscalationLevelModal } from './AddEscalationLevelModal';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (userId: string, userData: any) => Promise<void>;
  availableUsers: ProjectUserInterface[];
  projectUsers: ProjectUserInterface[];
  roles: any[];
  escalationLevels?: any[];
  projectId: string;
  onEscalationLevelsUpdate?: () => Promise<void>;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  project_role_id: string;
  escalation_level_id: string;
  department: string;
  status: string;
  skills: string;
}

// Invitation method types
type InvitationMethod = 'email' | 'sms' | 'phone';

// Function for phone normalization and validation
const normalizeAndValidatePhone = (phone: string): { isValid: boolean; normalizedPhone: string; error?: string } => {
  // Remove all spaces, dashes, parentheses and dots
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  
  // If phone number is empty
  if (!cleanPhone) {
    return { isValid: false, normalizedPhone: '', error: 'Phone number is required' };
  }
  
  // Case 1: Number starts with + but without US country code (e.g. +6504415356)
  if (/^\+[2-9]\d{9}$/.test(cleanPhone)) {
    const fixedPhone = '+1' + cleanPhone.substring(1);
    return { isValid: true, normalizedPhone: fixedPhone };
  }
  
  // Case 2: Regular US number without + (e.g. 6504415356 or 16504415356)
  if (/^\d{10}$/.test(cleanPhone)) {
    return { isValid: true, normalizedPhone: '+1' + cleanPhone };
  }
  
  if (/^1\d{10}$/.test(cleanPhone)) {
    return { isValid: true, normalizedPhone: '+' + cleanPhone };
  }
  
  // Case 3: Already correct international format
  if (/^\+[1-9]\d{6,14}$/.test(cleanPhone)) {
    return { isValid: true, normalizedPhone: cleanPhone };
  }
  
  // Case 4: Number starts with + but is incorrect
  if (cleanPhone.startsWith('+')) {
    return { 
      isValid: false, 
      normalizedPhone: cleanPhone,
      error: 'Invalid international phone format. For US numbers use format like +16504415356'
    };
  }
  
  // Other cases
  return { 
    isValid: false, 
    normalizedPhone: cleanPhone,
    error: 'Phone number must be 10 digits (US) or international format (+country code + number)'
  };
};

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onAddUser,
  roles,
  escalationLevels = [],
  projectId,
  onEscalationLevelsUpdate
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    project_role_id: '',
    escalation_level_id: '',
    department: '',
    status: 'Active',
    skills: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [selectedInvitationMethod, setSelectedInvitationMethod] = useState<InvitationMethod>('email');
  const [showAddEscalationLevelModal, setShowAddEscalationLevelModal] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time phone validation and normalization
    if (name === 'phone') {
      setPhoneError('');
      if (value) {
        const phoneValidation = normalizeAndValidatePhone(value);
        if (!phoneValidation.isValid) {
          setPhoneError(phoneValidation.error || 'Invalid phone number');
        } else {
          // Automatically update field with normalized number
          setFormData(prev => ({
            ...prev,
            phone: phoneValidation.normalizedPhone
          }));
          return; // Exit early to avoid setting value twice
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields (First Name, Last Name, Email, Phone)');
      return;
    }

    // Validate and normalize phone format
    const phoneValidation = normalizeAndValidatePhone(formData.phone);
    if (!phoneValidation.isValid) {
      const errorMsg = phoneValidation.error || 'Invalid phone number';
      toast.error(errorMsg);
      setPhoneError(errorMsg);
      return;
    }
    
    // Use normalized phone number
    const normalizedFormData = {
      ...formData,
      phone: phoneValidation.normalizedPhone
    };

    setIsLoading(true);
    try {
      // Prepare data for API - convert empty skills to null
      const apiData = {
        ...normalizedFormData,
        skills: normalizedFormData.skills.trim() === '' ? null : normalizedFormData.skills
      };
      
      console.log('AddUserModal: Original form data:', formData);
      console.log('AddUserModal: Normalized form data:', normalizedFormData);
      console.log('AddUserModal: API data being sent:', apiData);
      console.log('AddUserModal: Phone validation result:', phoneValidation);
      
      // Use email as userId for the API call (since we're creating a new user)
      await onAddUser('', apiData);
      
      console.log('AddUserModal: user added successfully');
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        project_role_id: '',
        escalation_level_id: '',
        department: '',
        status: 'Active',
        skills: ''
      });
      setPhoneError('');
      
      // Close modal with slight delay to ensure all state updates complete
      setTimeout(() => {
      onClose();
        toast.success('User added successfully');
      }, 100);
    } catch (error) {
      console.error('AddUserModal: failed to add user', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add user';
      toast.error(errorMessage)
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvitationMethodChange = (method: InvitationMethod) => {
    if (method === 'email') {
      setSelectedInvitationMethod(method);
    } else {
      toast.info('This feature is in development');
    }
  };

  const handleEscalationLevelCreated = async (_newLevel: any) => {
    // Close the modal
    setShowAddEscalationLevelModal(false);
    toast.success('Escalation level created successfully');
    
    // Update escalation levels list
    if (onEscalationLevelsUpdate) {
      await onEscalationLevelsUpdate();
    }
  };

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['status'] // Exclude auto-filled fields
  );

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 z-[60] text-gray-400 hover:text-gray-600 p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add Team Member</h2>
        </div>

        {/* Invitation Method Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Invitation Method
          </label>
          <div className="grid grid-cols-3 gap-3">
            {/* Email Option */}
            <div 
              onClick={() => handleInvitationMethodChange('email')}
              className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedInvitationMethod === 'email' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  selectedInvitationMethod === 'email' ? 'bg-blue-500' : 'bg-gray-400'
                }`}>
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className={`text-sm font-medium ${
                  selectedInvitationMethod === 'email' ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Email
                </span>
              </div>
            </div>

            {/* SMS Option */}
            <div 
              onClick={() => handleInvitationMethodChange('sms')}
              className="relative p-4 border-2 border-gray-200 rounded-lg cursor-pointer transition-all hover:border-gray-300 opacity-60"
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-400">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">SMS</span>
              </div>
              <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                In dev
              </div>
            </div>

            {/* Phone Option */}
            <div 
              onClick={() => handleInvitationMethodChange('phone')}
              className="relative p-4 border-2 border-gray-200 rounded-lg cursor-pointer transition-all hover:border-gray-300 opacity-60"
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-400">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">Phone</span>
              </div>
              <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                In dev
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Required Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="6504415356 (will auto-format to +16504415356)"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                phoneError 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              required
            />
            {phoneError && (
              <p className="mt-1 text-sm text-red-600">{phoneError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Enter US phone number (will auto-format): 6504415356 → +16504415356. For international: +44123456789
            </p>
          </div>

          {/* Optional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                name="project_role_id"
                value={formData.project_role_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a role</option>
                {(roles || []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Escalation Level
              </label>
              <select
                name="escalation_level_id"
                value={formData.escalation_level_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a level</option>
                {(escalationLevels || []).map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {escalationLevels.length === 0 ? (
                  <>
                    No escalation levels found. Click <button 
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`/projects/${projectId}?tab=team`);
                        toast.info('Redirecting to Escalation Rules to create your first level');
                      }}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      here
                    </button> to go to Escalation Rules and create your first level
                  </>
                ) : (
                  <>
                    To create a level click <button 
                      type="button"
                      onClick={() => setShowAddEscalationLevelModal(true)}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      here
                    </button> and open "Add Escalation Level"
                  </>
                )}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Skills
            </label>
            <textarea
              name="skills"
              value={formData.skills}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter skills separated by commas"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.first_name || !formData.last_name || !formData.email || !formData.phone || phoneError.length > 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Adding...' : 'Add User'}
            </button>
          </div>
        </form>
      </div>

      {/* Add Escalation Level Modal */}
      <AddEscalationLevelModal
        isOpen={showAddEscalationLevelModal}
        onClose={() => setShowAddEscalationLevelModal(false)}
        onAddLevel={handleEscalationLevelCreated}
        projectId={projectId}
      />
    </div>
  );
}; 